import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { formatTotalPayWithShiftBoost } from "@src/appV2/redesign/Shift/ShiftBoost/formatTotalPayWithShiftBoost";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { useMemo } from "react";

import { formatArrivalTime } from "../../ArrivalTime/formatArrivalTime";
import { SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getShiftPayRates } from "../../utils/calculateShiftPay";
import { getShiftTimingText } from "../../WorkerShifts/ShiftDetails/utils/getShiftTimingText";
import { useDistanceToWorkplace } from "../../Workplace/useDistanceToWorkplace";
import { convertModalShiftDataToUseBookShiftParams } from "../Booking/convertModalShiftDataToUseBookShiftParams";
import { PreBookingCheck, useBookShift } from "../Booking/useBookShift";
import { ConfirmBookingBottomSheet } from "./ConfirmBookingBottomSheet";
import { useMinuteCountdown } from "./useMinuteCountdown";

export interface ConfirmBookingBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  rateNegotiationId?: string;
}

export function ConfirmBookingBottomSheetContainer(props: ConfirmBookingBottomSheetContainerProps) {
  const { modalState, shift, rateNegotiationId } = props;

  const {
    id: shiftId,
    workplace,
    offer,
    attributes: { start, end, isInstantPay, isUrgent },
  } = shift;

  const {
    id: workplaceId,
    attributes: { name: workplaceName, location: workplaceLocation, requiresLunchBreak },
  } = workplace;
  const { timezone: workplaceTimezone } = workplaceLocation;

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const minutesUntilShiftStart = useMinuteCountdown({ date: startDate });
  const minutesElapsed = minutesUntilShiftStart < 0 ? Math.abs(minutesUntilShiftStart) : 0;
  const {
    navigateToModal,
    availableShiftBoost,
    computedArrivalData,
    isIndependentContractorBadgesRequired,
  } = useShiftModalsDataContext();
  const useShiftBoost = isDefined(availableShiftBoost) && isDefined(offer?.attributes.totalPay);

  const targetArrivalTime = computedArrivalData?.targetArrivalTime;

  const { totalPay, hourlyPay, totalPayFormatted, hourlyPayFormatted } = getShiftPayRates({
    shift,
    rateNegotiationId,
  });

  const totalPayWithElapsedTime =
    totalPay > 0 && hourlyPay > 0 ? totalPay - (minutesElapsed / 60) * hourlyPay : undefined;

  const totalWithBoostPay =
    (formatTotalPayWithShiftBoost(
      totalPayWithElapsedTime,
      availableShiftBoost?.attributes.amount
    ) ?? isDefined(totalPayWithElapsedTime))
      ? formatDollarsAsUsd(totalPayWithElapsedTime ?? 0)
      : totalPayFormatted;

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: workplaceLocation,
  });

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: Boolean(rateNegotiationId),
      });
    },
    computedArrivalData,
    rateNegotiationId,
  });

  const {
    attemptBookingShift,
    isBooking,
    isLoading: isLoadingUseBookShift,
  } = useBookShift(bookShiftParams);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_CONFIRM_BOOKING_SHEET_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const isShiftWithin24Hours = minutesUntilShiftStart <= 24 * 60;
  const isUrgentOrWithin24Hours = isUrgent || isShiftWithin24Hours;

  const urgentPillLabel = useMemo(() => {
    if (isDefined(targetArrivalTime)) {
      return `Please arrive by ${formatArrivalTime(
        utcToZonedTime(targetArrivalTime, workplaceTimezone ?? "America/Los_Angeles")
      )}`;
    }

    if (minutesUntilShiftStart <= 0) {
      return "Please arrive as soon as possible";
    }

    if (isUrgentOrWithin24Hours) {
      return getShiftTimingText({
        currentDate: new Date(),
        shiftStartDate: parseISO(start),
      });
    }

    return undefined;
  }, [
    isUrgentOrWithin24Hours,
    start,
    targetArrivalTime,
    workplaceTimezone,
    minutesUntilShiftStart,
  ]);

  const handleConfirmBooking = async () => {
    await attemptBookingShift({
      skipChecks: [PreBookingCheck.BOOKING_CONFIRMATION],
    });
  };

  return (
    <ConfirmBookingBottomSheet
      modalState={modalState}
      shiftStart={startDate}
      shiftEnd={endDate}
      timezone={workplaceTimezone ?? "America/Los_Angeles"}
      mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={requiresLunchBreak ?? true}
      urgentPillLabel={urgentPillLabel}
      workplaceName={workplaceName}
      formattedDistance={formattedDistance}
      hourlyPay={hourlyPayFormatted}
      totalWithBoostPay={totalWithBoostPay}
      useShiftBoost={useShiftBoost}
      shiftBoostAmount={availableShiftBoost?.attributes.amount}
      isInstantPay={isInstantPay ?? false}
      isLoading={isLoadingUseBookShift || isBooking || !isDefined(offer)}
      isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
      onConfirmBooking={handleConfirmBooking}
    />
  );
}
